import { Component } from '@theme/component';

/**
 * Drives the info popover in snippets/popover-info.liquid.
 *
 * Open state is the `data-open` attribute; CSS does the showing and the transition. This handles
 * the parts CSS can't: opening on hover or activation, closing (button, Escape, outside click),
 * and nudging the panel back inside the viewport when it would overflow — with the arrow
 * counter-shifted by the same amount so it stays on the trigger.
 *
 * Opening and closing are bound declaratively in the markup (`on:click`, `on:keydown:Escape`).
 * Only the pointer and outside-click behaviour is wired here, since neither has a markup form.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The info icon.
 * @property {HTMLElement} panel - The popover panel.
 *
 * @extends {Component<Refs>}
 */
class CustomPopover extends Component {
  requiredRefs = ['trigger', 'panel'];

  /** Distance the panel keeps from the viewport edge. */
  #viewportMargin = 16;
  #hoverDelay = 200;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#hoverOpen);
    this.addEventListener('pointerleave', this.#hoverClose);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get #isOpen() {
    return this.hasAttribute('data-open');
  }

  show = () => {
    clearTimeout(this.#timer);
    this.#position();
    this.setAttribute('data-open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
  };

  hide = () => {
    clearTimeout(this.#timer);
    this.removeAttribute('data-open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  };

  toggle = () => {
    this.#isOpen ? this.hide() : this.show();
  };

  /** Escape should leave focus somewhere sensible, not nowhere. */
  dismiss = () => {
    if (!this.#isOpen) return;
    this.hide();
    this.refs.trigger.focus();
  };

  #hoverOpen = () => {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(this.show, this.#hoverDelay);
  };

  #hoverClose = () => {
    clearTimeout(this.#timer);
    // Focus keeps it open for keyboard users even once the pointer leaves.
    if (this.contains(document.activeElement)) return;
    this.#timer = setTimeout(this.hide, this.#hoverDelay);
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#isOpen) return;
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.hide();
  };

  /**
   * Shifts the panel back inside the viewport when centring it on the trigger would overflow.
   * The panel is measurable while closed (it is `visibility: hidden`, not `display: none`), so
   * this runs before opening and the panel never appears in the wrong place first.
   */
  #position() {
    const { panel } = this.refs;

    this.style.removeProperty('--panel-shift');

    const rect = panel.getBoundingClientRect();
    const margin = this.#viewportMargin;
    let shift = 0;

    if (rect.left < margin) {
      shift = margin - rect.left;
    } else if (rect.right > window.innerWidth - margin) {
      shift = window.innerWidth - margin - rect.right;
    }

    if (shift) this.style.setProperty('--panel-shift', `${Math.round(shift)}px`);
  }
}

if (!customElements.get('custom-popover')) {
  customElements.define('custom-popover', CustomPopover);
}
